import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} [progress] - The reading progress bar wrapper.
 * @property {HTMLElement} [fill] - The part of the bar that grows.
 * @property {HTMLElement} [count] - "2 of 6" chip.
 * @property {HTMLElement} [current] - The number inside the chip.
 * @property {HTMLElement} [total] - The step count inside the chip.
 * @property {HTMLElement} [cta] - The sticky bar shown after the article.
 * @property {HTMLAnchorElement} [ctaLink] - Its button.
 */

/**
 * Reading chrome for article pages. While the article is being read, a bar
 * across the top fills as the reader moves through it. Once the reading ends
 * (the `[data-article-end]` marker), the bar steps aside and a sticky button
 * offers the way on: to the page's product section when it has one,
 * otherwise to the button's own link (the PDP). It hides while that section,
 * or any inline call-to-action (`[data-article-cta]`, snippets/cta-pill), is
 * on screen, so the reader never sees the same button twice.
 *
 * @extends {Component<Refs>}
 */
class ArticleProgress extends Component {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {IntersectionObserver | undefined} */
  #targetObserver;
  /** The on-screen targets the sticky button defers to */
  /** @type {Set<Element>} */
  #visibleTargets = new Set();
  /** @type {Element | null} */
  #end = null;
  /** @type {Element[]} */
  #steps = [];
  /** @type {Element | null} */
  #pageWrapper = null;
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#end = document.querySelector('[data-article-end]');
    if (!this.#end) return;

    this.#pageWrapper = document.querySelector('.page-wrapper');
    this.#steps = [...document.querySelectorAll('[data-article-step]')];
    if (this.refs.total) this.refs.total.textContent = String(this.#steps.length);
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const form = document.querySelector('main form[action*="/cart/add"]');
    const productSection = form?.closest('.shopify-section');
    if (productSection && this.refs.ctaLink) this.refs.ctaLink.href = `#${productSection.getAttribute('id')}`;

    const targets = [...document.querySelectorAll('main [data-article-cta]')];
    if (productSection) targets.push(productSection);
    if (targets.length > 0) {
      this.#targetObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#visibleTargets.add(entry.target);
          else this.#visibleTargets.delete(entry.target);
        }
        this.#requestUpdate();
      });
      for (const target of targets) this.#targetObserver.observe(target);
    }

    // Captured on the document because scroll events do not bubble and, on
    // desktop, the page scrolls inside `.page-wrapper` rather than the window.
    document.addEventListener('scroll', this.#requestUpdate, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#requestUpdate, { passive: true, signal });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#targetObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #requestUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    if (!this.#end) return;

    // Positions are read fresh each frame: lazy images keep moving the end
    // of the article while the page loads.
    const middle = window.innerHeight / 2;
    const scrolled = Math.max(window.scrollY, this.#pageWrapper?.scrollTop ?? 0);
    const distance = this.#end.getBoundingClientRect().top + scrolled - middle;
    const progress = distance > 0 ? Math.min(Math.max(scrolled / distance, 0), 1) : 1;
    const finished = progress >= 1;

    const { progress: bar, fill, count, current, cta } = this.refs;
    if (fill) fill.style.setProperty('--article-progress', String(progress));
    if (bar) bar.dataset.visible = String(!finished && scrolled > 8);

    if (count && current && this.#steps.length > 0) {
      const reached = this.#steps.filter((step) => step.getBoundingClientRect().top < middle).length;
      current.textContent = String(Math.max(reached, 1));
      count.dataset.visible = String(reached > 0 && !finished);
    }

    if (cta) {
      const show = finished && this.#visibleTargets.size === 0;
      cta.dataset.visible = String(show);
      cta.inert = !show;
    }
  }
}

if (!customElements.get('article-progress-component')) {
  customElements.define('article-progress-component', ArticleProgress);
}
